export type IdentityType = "individual" | "business";

export const KycStatus = {
  PENDING: "PENDING",
  IN_REVIEW: "IN_REVIEW",
  VERIFIED: "VERIFIED",
  REJECTED: "REJECTED",
  EXPIRED: "EXPIRED",
} as const;
export type KycStatus = (typeof KycStatus)[keyof typeof KycStatus];

export const PublicAccountPaymentMethod = {
  BANK: "BANK",
  CRYPTO_WALLET: "CRYPTO_WALLET",
  BINANCE_PAY: "BINANCE_PAY",
} as const;
export type PublicAccountPaymentMethod =
  (typeof PublicAccountPaymentMethod)[keyof typeof PublicAccountPaymentMethod];

// ---------------------------------------------------------------------------
// Paginated wrapper
// ---------------------------------------------------------------------------

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

// ---------------------------------------------------------------------------
// Raw API shapes — GET /internal/clients (qa). The backend returns the
// ClientEntity row (tenant contact data) with the shared `identity` relation
// joined. KYC state lives on `identity.kyc_provider` and is NOT joined in the
// list query today, so list items have no KYC status in QA (see
// docs/clients-view-gaps.md).
// ---------------------------------------------------------------------------

export interface ApiIdentityKycProvider {
  status: KycStatus | null;
  provider_applicant_id?: string | null;
  verified_at?: string | null;
  rejection_reason?: string | null;
  documents?: Array<{ name: string; url: string }> | null;
  /**
   * Provider registration metadata (jsonb). On qa the provider registration stores its hosted link under
   * `websdk_link`, but the UI never surfaces it — user-facing links come only from the verification engine
   * (VerificationService.issueLink), so the provider's own flow/URL is never shown.
   */
  metadata?: Record<string, unknown> | null;
}

export interface ApiClientIdentity {
  id: string;
  type: IdentityType;
  country: string;
  identification_type: string;
  identification_number: string;
  /** Individual first name; for BUSINESS identities the backend stores the legal name here. */
  first_name: string;
  last_name?: string | null;
  birthdate?: string | null;
  gender?: string | null;
  nationality?: string | null;
  business_type?: string | null;
  incorporation_date?: string | null;
  logo?: string | null;
  owner_name?: string | null;
  kyc_provider?: ApiIdentityKycProvider | null;
  created_at: string;
  updated_at: string;
}

export interface ApiClientListItem {
  id: string;
  organization_id: string | null;
  identity_id: string;
  email: string | null;
  phone: string | null;
  country: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  identity: ApiClientIdentity | null;
}

export type ClientsListResponse = PaginatedResponse<ApiClientListItem>;

// ---------------------------------------------------------------------------
// Normalized list item — flat view model produced by `normalizeClientListItem`
// (utils/normalizeClient.ts) from the raw nested shape above. UI components
// consume this, never the raw item.
// ---------------------------------------------------------------------------

export interface ClientListItem {
  id: string;
  organization_id: string | null;
  /** Shared identity this client points to — drives the verification engine (marco C). */
  identity_id: string | null;
  type: IdentityType;
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  identification_type: string | null;
  identification_number: string | null;
  /** Null when the list endpoint does not join `identity.kyc_provider` (current QA behaviour). */
  kyc_status: KycStatus | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface OrganizationSearchItem {
  id: string;
  name?: string | null;
}

export type OrganizationsSearchResponse = PaginatedResponse<OrganizationSearchItem>;

// ---------------------------------------------------------------------------
// Client detail — GET /internal/clients/:id
// ---------------------------------------------------------------------------

export interface ClientAccount {
  id: string;
  client_id: string;
  organization_id?: string | null;
  bank_name?: string | null;
  currency?: string | null;
  payment_method?: PublicAccountPaymentMethod | null;
  account_metadata: Record<string, unknown>;
  account_key?: string | null;
  status?: string | null;
  alias?: string | null;
  is_active?: boolean;
  created_at: string;
  deleted_at?: string | null;
}

/** Raw detail — GET /internal/clients/:id (ClientEntity + identity + identity.kyc_provider + accounts). */
export interface ApiClientDetail extends ApiClientListItem {
  accounts: ClientAccount[];
  activeKycLinkExpiresAt: string | null;
  activeKycLinkVerificationUrl: string | null;
}

/**
 * Raw — GET /internal/clients/organizations/:id/client-profile. The organization rendered in a client-detail
 * shape (BUSINESS type, no personal fields) with its REAL corporate KYC data — unlike the tree detail
 * endpoint, which hardcodes `kyc_status: null` at the organization level on qa.
 */
export interface ApiOrganizationClientProfile {
  id: string;
  organization_id: null;
  /** Corporate identity id — not returned by the endpoint today; resolved via the verification engine. */
  identity_id?: string | null;
  type: IdentityType;
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  identification_type: string | null;
  identification_number: string | null;
  kyc_status: KycStatus | null;
  sumsub_applicant_id: string | null;
  kyc_verified_at: string | null;
  kyc_rejection_reason: string | null;
  kyc_documents: Array<{ name: string; url: string }> | null;
  accounts: ClientAccount[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  activeKycLinkExpiresAt: string | null;
  activeKycLinkVerificationUrl: string | null;
}

/** Normalized detail — flat view model produced by `normalizeClientDetail` (utils/normalizeClient.ts). */
export interface ClientByIdResponse {
  id: string;
  organization_id: string | null;
  /**
   * Shared identity this client points to — drives the verification engine (marco C). Null on the
   * admin/org-tree path.
   */
  identity_id: string | null;
  type: IdentityType;
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  identification_type: string | null;
  identification_number: string | null;
  birthdate: string | null;
  incorporation_date: string | null;
  kyc_status: KycStatus | null;
  sumsub_applicant_id: string | null;
  kyc_verified_at: string | null;
  kyc_rejection_reason: string | null;
  kyc_documents: Array<{ name: string; url: string }> | null;
  accounts: ClientAccount[];
  activeKycLinkExpiresAt: string | null;
  activeKycLinkVerificationUrl: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// ---------------------------------------------------------------------------
// Identity payloads — mirror of the backend IndividualIdentityDto /
// BusinessIdentityDto (coloca-nest qa branch). The ValidationPipe uses
// `forbidNonWhitelisted`, so these must contain ONLY the declared fields.
// Clients-local copy: kept independent from the cards module on purpose.
// ---------------------------------------------------------------------------

export interface IndividualIdentityRequest {
  email: string;
  phone: string; // international, WITHOUT the + symbol (@IsInternationalPhone)
  first_name: string;
  last_name: string;
  birthdate: string; // YYYY-MM-DD
  gender: "M" | "F" | "OTHER";
  country: string; // ISO3 — document-issuing country (part of the dedupe key)
  nationality?: string; // ISO3 — optional; backend defaults to `country`
  identification_number: string;
  identification_type: string; // valid per country, category "all" (CC, NIT, DNI, ...)
  metadata?: Record<string, unknown>;
}

export interface BusinessIdentityRequest {
  legal_name: string;
  business_type: string;
  country: string; // ISO3 — company country (part of the dedupe key)
  identification_number: string; // letters, numbers, ".", "-", space
  identification_type: string; // valid per country, category "all" (NIT, RFC, ...)
  incorporation_date: string; // YYYY-MM-DD
  email: string;
  phone: string; // international, WITHOUT the + symbol
  owner_name: string;
  owner_id_type: string; // personal doc type, valid per owner_country_code
  owner_id_number: string;
  owner_address_line1: string;
  owner_city: string;
  owner_state: string;
  owner_postal_code: string;
  owner_country_code: string; // ISO3
  metadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Create client — POST /internal/clients
// Mirror of CreateClientDto: `identity` is REQUIRED and there is NO `address`
// at the root. Root `phone` carries the + symbol (tenant copy); identity.phone
// must NOT. `organization_id` is overridden by the JWT scope for org-scoped
// callers, so it is only sent for the subclient (internal) flow.
// ---------------------------------------------------------------------------

export interface CreateClientBody {
  type: IdentityType;
  organization_id?: string;
  identity: IndividualIdentityRequest | BusinessIdentityRequest;
  email?: string; // tenant contact copy
  phone?: string; // international, WITH the + symbol
  country?: string; // ISO3 (backend stores country ?? identity.country)
}

// ---------------------------------------------------------------------------
// Update client — PATCH /internal/clients/:id
// Mirror of UpdateClientDto (qa): tenant contact data ONLY. Identity fields
// (names, document, birthdate…) live on the shared `identities` table and are
// rejected by the global ValidationPipe (`forbidNonWhitelisted`) — never send
// them here.
// ---------------------------------------------------------------------------

export interface UpdateClientBody {
  email?: string;
  phone?: string;
  country?: string;
}

// ---------------------------------------------------------------------------
// Update organization — PATCH /internal/organizations/:id
// ---------------------------------------------------------------------------

export interface UpdateOrganizationBody {
  name?: string;
  email?: string;
  phone?: string;
  country?: string;
}

// ---------------------------------------------------------------------------
// Create organization — POST /internal/organizations
// Mirror of CreateOrganizationDto (qa): `identity` (BusinessIdentityDto) is now
// REQUIRED — the backend find-or-creates the corporate identity by its dedupe
// key, links it via organizations.identity_id and registers it with the KYC
// provider, all in one transaction. There is NO `address` field: the backend
// derives the KYC address from the identity's owner_* fields.
// ---------------------------------------------------------------------------

export type OrganizationStatus = "active" | "inactive" | "suspended" | "pending";

export interface CreateOrganizationBody {
  name: string; // 2–255
  email: string; // globally unique (409 on duplicate)
  /** Corporate identity of the organization itself (company + legal representative). */
  identity: BusinessIdentityRequest;
  slug?: string; // globally unique; auto-generated from name when omitted
  phone?: string; // international, WITHOUT the + symbol
  status?: OrganizationStatus; // backend service defaults to "active"
  country?: string; // ISO3
  type?: IdentityType;
}

/** Organization entity returned by POST /internal/organizations (relevant subset). */
export interface CreateOrganizationResponse {
  id: string;
  name: string;
  slug?: string | null;
  email: string;
  phone?: string | null;
  status: OrganizationStatus;
  organization_identity_id?: string | null;
  country?: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Create client account — POST /internal/clients/:clientId/accounts
// ---------------------------------------------------------------------------

export interface CreateClientAccountRequest {
  bank_name: string;
  currency: string;
  payment_method: PublicAccountPaymentMethod;
  alias?: string;
  account_metadata: Record<string, unknown>;
}

export type ListClientAccountsResponse = ClientAccount[];

// ---------------------------------------------------------------------------
// Internal mail — POST /internal/mail/send
// ---------------------------------------------------------------------------

export type OperationsKycVerificationMailType = "OPERATIONS_KYC_VERIFICATION";

/** Nested payload for the KYC verification email (operations template). */
export interface OperationsKycVerificationMailPayload {
  name: string;
  verificationUrl: string;
  /** Hours remaining until the link expires (numeric string; "" when there is no expiration). */
  expiresInHours: string;
}

export interface SendKycVerificationMailRequest {
  type: OperationsKycVerificationMailType;
  to: string;
  payload: OperationsKycVerificationMailPayload;
}

// ---------------------------------------------------------------------------
// Tree view (admin) — GET /internal/clients/tree
// These types remain for the internal admin tree endpoint.
// ---------------------------------------------------------------------------

export interface ClientTreeItem {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  accountIds: string[];
}

export interface OrganizationTreeItem {
  id: string;
  name: string;
  email: string;
  type: IdentityType | null;
  kyc_status: KycStatus | null;
  country: string | null;
  created_at: string;
  kyc_documents: Array<{ name: string; url: string }> | null;
  clients: ClientTreeItem[];
  accountIds: string[];
}

export type InternalClientsTreeResponse = PaginatedResponse<OrganizationTreeItem>;

export interface OrganizationDetailClientItem {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  type?: IdentityType | null;
  email?: string | null;
  kyc_status: KycStatus | null;
  phone?: string | null;
  country?: string | null;
  created_at: string;
  kyc_documents?: Array<{ name: string; url: string }> | null;
  accountIds: string[];
  activeKycLinkExpiresAt?: string | null;
  activeKycLinkVerificationUrl?: string | null;
  sumsub_applicant_id?: string | null;
}

export interface OrganizationDetailResponse {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  type: IdentityType | null;
  kyc_status: KycStatus | null;
  country: string | null;
  created_at: string;
  kyc_documents?: Array<{ name: string; url: string }> | null;
  clients: OrganizationDetailClientItem[];
  accountIds: string[];
  activeKycLinkExpiresAt?: string | null;
  activeKycLinkVerificationUrl?: string | null;
  sumsub_applicant_id?: string | null;
}

// ---------------------------------------------------------------------------
// Legacy aliases (used by ClientsTable and other consumers)
// ---------------------------------------------------------------------------

export type ClientKycStatus = KycStatus;
export type TreeClientListItem = OrganizationTreeItem;
export type SubClientSummary = ClientTreeItem;
